import { Box, CircularProgress, Divider, TableSortLabel, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as Empty } from "../../assets/icon/Empty.svg";
import { Column } from "../../utils/constants";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#ba2156",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));
interface Props {
  header: Column[];
  data: any;
  isLoading?: boolean;
  // External pagination props (optional)
  page?: number;
  rowsPerPage?: number;
  totalElements?: number;
  onPageChange?: (event: unknown, newPage: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  onSortChange?: (field: string) => void;
}
const MuiTable = (props: Props) => {
  const { i18n, t } = useTranslation();
  const [internalPage, setInternalPage] = React.useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = React.useState(20);

  // Use external pagination if provided, otherwise use internal
  const page = props.page !== undefined ? props.page : internalPage;
  const rowsPerPage = props.rowsPerPage !== undefined ? props.rowsPerPage : internalRowsPerPage;
  const totalElements = props.totalElements !== undefined ? props.totalElements : (props.data ? props.data.length : 0);

  const handleChangePage = (event: unknown, newPage: number) => {
    if (props.onPageChange) {
      props.onPageChange(event, newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (props.onRowsPerPageChange) {
      props.onRowsPerPageChange(event);
    } else {
      setInternalRowsPerPage(+event.target.value);
      setInternalPage(0);
    }
  };

  return (
    <Box sx={{
        borderRadius: 5
    }}>
      <TableContainer  className="rounded-t-lg">
        <Table stickyHeader aria-label="customized table">
          <TableHead className="rounded-t-lg">
            <TableRow>
              {props.header.map((column, index) => (
                <StyledTableCell
                  key={index}
                  align={column.align}
                  style={{ minWidth: column.minWidth, width: column.width || column.width }}
                >
                  {column.sortable && props.onSortChange ? (
                    <TableSortLabel
                      active={props.sortField === column.id}
                      direction={
                        props.sortField === column.id
                          ? props.sortDirection
                          : "asc"
                      }
                      onClick={() => props.onSortChange?.(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.isLoading ? (
              <TableRow>
                <TableCell colSpan={props.header.length} align="center">
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "200px", // Adjust height as needed
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : props.data.length > 0 ? (
              // If using external pagination, don't slice (server already paginated)
              // If using internal pagination, slice the data
              (props.page !== undefined ? props.data : props.data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage))
                .map((row: any, index: number) => {
                  return (
                    <StyledTableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                    >
                      {props.header.map((column) => {
                        let value = row[column.id];
                        return (
                          <StyledTableCell key={column.id} align={column.align}>
                            {column.format ? column.format(value, row) : value}
                          </StyledTableCell>
                        );
                      })}
                    </StyledTableRow>
                  );
                })
            ) : (
              <TableRow>
                <TableCell colSpan={props.header.length} align="center">
                  <Box className="flex flex-col justify-center items-center">
                    <Empty />
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: "text.secondary" }}
                    >
                      {t("common.no_data")}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider></Divider>
      <TablePagination
        rowsPerPageOptions={[10, 20, 25, 100]}
        component="div"
        count={totalElements}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};
export default MuiTable;
